<template>
  <NuxtLayout navbar-color="#F9F8F5">
    <div v-if="story" class="event">
      <div class="bg-paper-100">
        <section class="event__hero container">
          <div class="event__logos">
            <BaseNuxtImage
              v-for="logo in story.content.logos"
              :key="logo.filename"
              :src="logo.filename"
              :alt="logo.alt"
              class="event__logo"
            />
          </div>
          <BaseBreadcrumbs class="event__breadcrumbs" />
          <h1 class="event__title" v-html="story.content.hero_title"></h1>
          <div class="event__hero-info">
            <div>
              <p>{{ story.content.hero_text }}</p>
              <StoryblokComponent
                v-for="button in story.content.button"
                :key="button._uid"
                :blok="button"
              />
            </div>

            <div class="event__info">
              <p class="event__info-item">
                <span class="event__info-title"
                  >{{ $t('common.events.date') }}:</span
                >
                <span>{{ story.content.date }}</span>
              </p>
              <p class="event__info-item">
                <span class="event__info-title"
                  >{{ $t('common.events.location') }}:</span
                >
                <span>{{ story.content.location }}</span>
              </p>
              <p v-if="story.content.booth" class="event__info-item">
                <span class="event__info-title"
                  >{{ $t('common.events.booth') }}:</span
                >
                <span>{{ story.content.booth }}</span>
              </p>
            </div>
          </div>
        </section>
      </div>
      <div class="pt-128 pb-128 bg-white" v-if="story.content.agenda_cards">
        <section class="container">
          <StoryblokComponent
            v-for="card in story.content.agenda_cards"
            :key="card._uid"
            :blok="card"
          />
        </section>
      </div>
      <div
        class="pt-128 pb-128 bg-paper-100"
        v-if="
          story.content.demo_section && story.content.demo_section.length > 0
        "
      >
        <section class="container">
          <StoryblokComponent
            v-for="card in story.content.demo_section"
            :key="card._uid"
            :blok="card"
          />
        </section>
      </div>
      <div
        class="pt-128 pb-128 bg-white"
        v-if="
          story.content.additional_section &&
          story.content.additional_section.length > 0
        "
      >
        <section class="container">
          <StoryblokComponent
            v-for="card in story.content.additional_section"
            :key="card._uid"
            :blok="card"
          />
        </section>
      </div>
      <div class="bg-paper-100">
        <section class="event__section container">
          <div class="event__section-header">
            <h2>{{ $t('common.events.our_experts') }}</h2>
            <p>{{ $t('common.events.experts_copy') }}</p>
          </div>

          <EventExperts
            :schedule-link="story.content.cta_link"
            :representatives="story.content.representatives"
          />
        </section>
      </div>
      <div class="bg-white">
        <StoryblokComponent
          v-for="section in data?.data.stories[0].content.body"
          :key="section._uid"
          :blok="section"
        />
      </div>
      <div class="bg-paper-100">
        <section class="event__section container">
          <div class="event__section-header">
            <h2>{{ $t('common.events.lets_meet') }}</h2>
            <p>{{ $t('common.events.lets_meet_copy') }}</p>
          </div>
          <EventCalendar
            v-if="story.content.calendar_embed_link"
            :iframe-source="story.content.calendar_embed_link"
          />
        </section>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { EventStoryblok } from '@/types'

const { locale } = useI18n()

const storyblokApi = useStoryblokApi()

const config = useRuntimeConfig()

const { slug } = useRoute().params

const [{ data }, { story }] = await Promise.all([
  useAsyncData(
    'events-data',
    async () =>
      await storyblokApi.get('cdn/stories', {
        version: config.public.storyblokApiVersion as 'draft' | 'published',
        starts_with: 'events/shared-section',
        language: locale.value,
      }),
  ),
  useFetchStory<EventStoryblok>(`events/${slug}`, {
    apiOptions: {
      resolve_relations: ['event.representatives'],
    },
  }),
]).catch((error) => {
  throw createError({
    message: error,
    statusCode: 404,
  })
})

onMounted(() => {
  const isEventPassed =
    Date.now() > new Date(story.value.content.end_date).getTime()

  if (isEventPassed) {
    navigateTo('/events')
  }
})

const { getBreadcrumbsJsonLd } = useBreadcrumbs()

useCustomHead(story, {
  additionalScripts: [
    'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js',
  ],
  jsonLd: getBreadcrumbsJsonLd(),
})
</script>

<style lang="scss" scoped>
.event {
  &__hero {
    padding-top: 56px;

    @media (min-width: $breakpoint-md) {
      padding-top: 96px;
    }
  }
  &__agenda {
    background-color: var(--white);
  }

  &__logos {
    display: flex;
    column-gap: 16px;
    padding-bottom: 32px;
    border-bottom: 1px solid var(--paper-400);
    margin-bottom: 20px;

    @media (min-width: $breakpoint-md) {
      padding-bottom: 48px;
    }
  }

  &__logo {
    max-height: 42px;
    margin-right: 1rem;
    width: auto;
  }

  &__title {
    margin-bottom: 32px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 48px;
    }
  }

  &__info {
    margin-top: 48px;

    @media (min-width: $breakpoint-lg) {
      margin-top: 0;
    }
  }

  &__info-title {
    font-weight: var(--font-weight-paragraph-bold);
    min-width: 82px;
    margin-right: 18px;
  }

  &__info-item {
    display: flex;
    padding: 12px 0;
    border-top: 1px solid var(--paper-400);
    margin: 0;

    &:last-child {
      border-bottom: 1px solid var(--paper-400);
    }
  }

  &__hero-info {
    display: grid;
    grid-template-columns: 1fr;
    padding-bottom: 80px;

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: 574px 1fr;
      column-gap: 106px;
      padding-bottom: 128px;
    }
  }

  &__section {
    padding-top: 80px;

    @media (min-width: $breakpoint-lg) {
      padding-top: 128px;
    }

    &:last-child {
      padding-bottom: 80px;

      @media (min-width: $breakpoint-lg) {
        padding-bottom: 128px;
      }
    }
  }

  &__section-header {
    max-width: 574px;

    > p {
      margin-bottom: 48px;
    }

    > h2 {
      margin-bottom: 20px;
    }
  }
}
</style>
